import { JSX, useState } from 'react';
import { Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useGetIdentity, UseGetIdentityResult, useNotify, useRefresh } from 'react-admin';
import { AdminMiniDialog } from '../../admin-mini/components/AdminMiniDialog';
import {
    AdminMiniComposer,
    AdminMiniRunDraft,
    emptyDraft,
} from '../../admin-mini/components/AdminMiniComposer';
import { COMPLIANCE_SNIFFER } from '../../admin-mini/admin-mini.descriptors';
import { AdminMiniSendReport } from '../../admin-mini/admin-mini.types';
import { useAdminMiniAccess } from '../../admin-mini/hooks/admin-mini-access';
import { useAdminMiniActivate } from '../../admin-mini/hooks/admin-mini-requests';
import { activationPayloadFor } from '../../admin-mini/admin-mini.payload';
import { createRandomString } from '../../admin-mini/admin-mini.rules';

const TOOL = COMPLIANCE_SNIFFER;

/**
 * Starting a compliance sniff from HQ.
 *
 * The twin of `CreateNewMysteryShopperModal`, and deliberately the same shape: both open the same
 * composer, build their payload through the same `activationPayloadFor`, and differ only in the
 * run's own reference and which route they post to.
 */
export const CreateComplianceSnifferModal: () => JSX.Element = (): JSX.Element => {
    const user: UseGetIdentityResult = useGetIdentity();
    const notify = useNotify();
    const refresh = useRefresh();
    const access = useAdminMiniAccess(TOOL);
    const { activating, activate } = useAdminMiniActivate(TOOL);

    const [open, setOpen] = useState<boolean>(false);
    const [draft, setDraft] = useState<AdminMiniRunDraft>(emptyDraft(TOOL));
    const [csCode, setCsCode] = useState<string>(createRandomString(10));
    const [report, setReport] = useState<AdminMiniSendReport | undefined>(undefined);

    function handleOpen(): void {
        setDraft(emptyDraft(TOOL));
        setCsCode(createRandomString(10));
        setReport(undefined);
        setOpen(true);
    }

    async function handleActivate(): Promise<void> {
        const answer: AdminMiniSendReport = await activate(activationPayloadFor(draft, {
            action: 'create_and_activate',
            adminUser: user?.data?.id,
            csCode: csCode,
        }));

        setReport(answer);
        notify(answer.message, { type: answer.status === 'success' ? 'info' : 'warning' });

        if (answer.status === 'success') {
            refresh();
            // Held open when some recipients failed, because the report names which and closing
            // takes that away before it is read.
            if ((answer.data?.failed ?? 0) === 0) {
                setOpen(false);
            }
        }
    }

    return (
        <>
            <Button variant="contained" startIcon={ <AddIcon /> } onClick={ handleOpen }>
                { 'New sniff' }
            </Button>

            <AdminMiniDialog open={ open }
                             onClose={ (): void => setOpen(false) }
                             title="New compliance sniff"
                             subtitle={ `Reference ${ csCode }` }>
                <AdminMiniComposer tool={ TOOL }
                                   draft={ draft }
                                   onChange={ setDraft }
                                   onActivate={ (): void => { void handleActivate(); } }
                                   activating={ activating }
                                   access={ access }
                                   report={ report } />
            </AdminMiniDialog>
        </>
    );
};
